import { useTranslation } from "react-i18next"
import { Grid, Typography } from "@mui/material"
import fp from "lodash/fp"

import type { ReactNode } from "react"
import type { Powerline } from "types/powerlines.types"

import CardWithTitle from "widgets/common/CardWithTitle"
import LabelValueStack from "widgets/common/LabelValueStack"
import SeasonalAmpacityBreakdown from "widgets/powerlines/SeasonalAmpacityBreakdown"
import { formatDateFromISO, formatDayFromISO } from "helpers/utils/dateUtils"
import { parseSeasonalAmpacity } from "helpers/utils/seasonalAmpacity"
import {
  formatAmpacity,
  formatFrequency,
  formatLength,
  formatValueCell,
  formatVoltage,
} from "helpers/utils/powerlines"

interface PowerlineDetailsProps {
  powerline: Powerline
}

interface Group {
  titleKey: string
  fields: { labelKey: string; value: ReactNode }[]
}

const PowerlineDetails = ({ powerline }: PowerlineDetailsProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  // Seasonal ampacity is a value per date window, so it renders as a breakdown
  // rather than a single number. The deprecated nominal is the fallback.
  const seasonal = powerline.ampacity_seasonal
  const nominal = formatValueCell(
    { value: powerline.ampacity_nominal, locale },
    formatAmpacity,
  )
  const ampacity =
    seasonal && parseSeasonalAmpacity(seasonal).length > 0 ? (
      <SeasonalAmpacityBreakdown
        seasonal={seasonal}
        entryProps={{ color: "text.disabled" }}
      />
    ) : (
      nominal
    )

  // A column each, so the nominals stay together however tall the ampacity gets.
  const groups: Group[] = [
    {
      titleKey: "powerlines.GENERAL",
      fields: [
        { labelKey: "powerlines.ID", value: powerline.id },
        {
          labelKey: "powerlines.INSTALLED_AT",
          value: formatDayFromISO(powerline.installed_at),
        },
        {
          labelKey: "powerlines.UPDATED_AT",
          value: formatDateFromISO(powerline.updated_at),
        },
      ],
    },
    {
      titleKey: "powerlines.NOMINAL_VALUES",
      fields: [
        {
          labelKey: "powerlines.VOLTAGE_NOMINAL",
          value: formatValueCell(
            { value: powerline.voltage_nominal, locale },
            formatVoltage,
          ),
        },
        {
          labelKey: "powerlines.FREQUENCY_NOMINAL",
          value: fp.isNil(powerline.frequency_nominal)
            ? undefined
            : formatFrequency(powerline.frequency_nominal, locale),
        },
        { labelKey: "powerlines.AMPACITY_NOMINAL", value: ampacity },
      ],
    },
    {
      titleKey: "powerlines.STRUCTURE",
      fields: [
        {
          labelKey: "powerlines.LENGTH",
          value: formatValueCell({ value: powerline.length, locale }, formatLength),
        },
        { labelKey: "powerlines.SPANS", value: powerline.span_ids.length },
        { labelKey: "powerlines.TOWERS", value: powerline.tower_ids.length },
      ],
    },
  ]

  return (
    <CardWithTitle titleKey="powerlines.POWERLINE_DETAILS">
      <Grid container columnSpacing={4} rowSpacing={3}>
        {groups.map(({ titleKey, fields }) => (
          <Grid item xs={12} md={4} key={titleKey}>
            <Typography variant="overlineDense" component="p" sx={{ mb: 2 }}>
              {t(titleKey)}
            </Typography>
            {fields.map(({ labelKey, value }) => (
              <LabelValueStack key={labelKey} labelKey={labelKey} value={value} />
            ))}
          </Grid>
        ))}
      </Grid>
    </CardWithTitle>
  )
}

export default PowerlineDetails
